import { Auth, API } from 'aws-amplify';

export const appVersions = [
    'chatsstats_filtered_month_cumulative'
]

/**
 * CustomersAPI implements the basis functions that allow to the administrators
 * users to manage the customers information, allowing to create a single customer, 
 * a bulk of new customers, update or search customers and delete customers.
 *
 * It uses the Auth and API libraries of AWS Amplify framework.
 * 
 * @class CustomersAPI
 */
export default class CustomersAPI {
    static async composeRequest() {
        return [
            'AdminQueries',
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': (await Auth.currentSession()).getAccessToken().getJwtToken()
                }
            }
        ]
    }

    static async createCustomer(name, email, clinics, report, app_version) {
        try {
            let endpoint = '/customer';
            let [ api, params ] = await this.composeRequest();
            params.body = {
                username: email,
                attributes: {
                    email,
                    name,
                    'custom:clinics': JSON.stringify(clinics),
                    'custom:sendReport': report && report.frecuency ? '1' : '0',
                    'custom:frecuency': report && report.frecuency ? report.frecuency.toString() : 'false',
                    'custom:accumulated': report && report.frecuency && report.accumulated ? '1' : '0',
                    'custom:app_version': app_version ? JSON.stringify(app_version) : '[]'
                }
            }
            const result = await API.post(api, endpoint, params);
            return [ true, result.message ];
        } catch (e) {
            return [ false, e.message ];
        }
    }

    static async createCustomers(customers) {
        const users = [];
        const { length } = customers;
        for (let i = 0; i < length; i++) {
            const { email, name, clinics, report, app_version } = customers[i];
            const [ success, result ] = await this.createCustomer(name, email, clinics, report, app_version);

            if (success) users.push(result);
            else console.info(result);
        }

        const success = users.length === length;
        const result = !success ? `${users.length} of ${length} customers created.` : users;
        return [ success, result ];
    }

    static async updateCustomer(email, name, clinics, report, app_version = []) {
        try {
            let endpoint = '/customer';
            let [ api, params ] = await this.composeRequest();
            params.body = {
                username: email,
                attributes: {
                    name,
                    'custom:clinics': JSON.stringify(clinics),
                    'custom:sendReport': report.frecuency ? '1' : '0',
                    'custom:frecuency': report.frecuency ? report.frecuency.toString() : 'false',
                    'custom:accumulated': report.frecuency && report.accumulated ? '1' : '0',
                    'custom:app_version': app_version ? JSON.stringify(app_version) : '[]'
                }
            }

            const result = await API.put(api, endpoint, params);
            return [ true, result.message ];
        } catch (e) {
            return [ false, e.message ];
        }
    }

    static async updateCustomerReport(email, report) {
        try {
            let endpoint = '/customer';
            let [ api, params ] = await this.composeRequest();
            params.body = {
                username: email,
                attributes: {
                    'custom:sendReport': report.frecuency ? '1' : '0',
                    'custom:frecuency': report.frecuency ? report.frecuency.toString() : 'false',
                    'custom:accumulated': report.frecuency && report.accumulated ? '1' : '0',
                }
            }

            const result = await API.put(api, endpoint, params);
            return [ true, result.message ];
        } catch (e) {
            return [ false, e.message ];
        }
    }

    static parseCustomer(cognitoUser) {
        const attributes = {};
        cognitoUser['Attributes'].forEach(attr => {
            attributes[attr['Name']] = attr['Value'];
        });

        let app_version, clinics;
        try {
            clinics = JSON.parse(attributes['custom:clinics']);
        } catch {
            clinics = [];
        }
        try {
            app_version = JSON.parse(attributes['custom:app_version']);
        } catch {
            app_version = [];
        }

        return {
            clinics,
            app_version,
            name: attributes['name'],
            email: attributes['email'],
            report: parseInt(attributes['custom:sendReport']) ? {
                accumulated: Boolean(parseInt(attributes['custom:accumulated'])),
                frecuency: attributes['custom:frecuency'] !== 'false' ? attributes['custom:frecuency'] : false,
            } : {}
        }
    }

    static async listCustomers(token) {
        const endpoint = '/customers';
        let [ api, params ] = await this.composeRequest();
        params.queryStringParameters = { 
            token,
            limit: 10
        };

        const { NextToken, Users } = await API.get(api, endpoint, params);
        const users = Users.map(this.parseCustomer);

        return [ users, NextToken ];
    }

    static async findCustomers(filter, token) {
        // eslint-disable-next-line no-unused-vars
        const params = {
            queryStringParameters : {
                Filter: filter,
                NextToken: token,
                Limit: 10
            }
        };

        try {
            const { Users, NextToken } = await API.get("chatsapi", '/search-customers', params);
            if(!Users) {
                return await this.findCustomersLegacy(filter, token);
            }
            const users = Users.map(this.parseCustomer);
            return [ users, NextToken ];
        } catch (error) {
            return await this.findCustomersLegacy(filter, token);
        }
        
    }

    static async findCustomersLegacy(filter, token) {
        const endpoint = '/search-customers';
        let [ api, params ] = await this.composeRequest();
        params.queryStringParameters = {
            filter,
            token,
            limit: 10
        };

        try {
            const { NextToken, Users } = await API.get(api, endpoint, params);
            const users = Users.map(this.parseCustomer);

            return [ users, NextToken ];
        } catch (error) {
            return [ false, error.message ];
        }

    }

    static async deleteCustomer(email) {
        try {
            const endpoint = '/customer';
            let [ api, params ] = await this.composeRequest();
            params.body = { 'username': email };

            const result = await API.del(api, endpoint, params);
            return [ true, result.message ];
        } catch (e) {
            return [ false, e.message ];
        }
    }
}